import { useDispatch, useSelector } from 'react-redux'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { setCredentials, logout } from '../store/slices/authSlice'
import { authService } from '../services/authService'

export const useAuth = () => {
  const dispatch = useDispatch()
  const { token, user, isAuthenticated } = useSelector((state) => state.auth)
  const queryClient = useQueryClient()



  const loginMutation = useMutation({
    mutationFn: async (credentials) => {
      // Use unified login - it will automatically route to owner login if owner detected
      return await authService.login(credentials)
    },
    onSuccess: async (data, variables) => {
      if (data.success && data.token) {
        const confirmationPending = localStorage.getItem('loginConfirmationPending') === 'true'
        
        // 1. Get authoritative user and role from backend response
        // The backend now returns { token, user: { id, username, role, ... } }
        const partialUser = data.user || {}
        let role = partialUser.role?.toLowerCase() || 'staff'
        
        // Ensure owner role is consistent
        if (role.includes('owner')) {
          role = 'owner'
        }

        // 2. Store token and partial user immediately
        dispatch(setCredentials({ 
          token: data.token,
          user: partialUser, 
          role: role
        }))
        
        console.log(`Login successful. Detected role: ${role}`)
        
        // 3. Fetch full profile (including wallet, etc.) if not pending confirmation
        if (!confirmationPending) {
          try {
            console.log(`Fetching full ${role} profile...`)
            const profileResponse = role === 'owner'
              ? await authService.getOwnerProfile() 
              : await authService.getProfile()
              
            console.log('Profile response:', profileResponse)
            
            // Extract full user data
            // Owner profile returns data directly, Staff profile returns data.user
            const fullUserData = role === 'owner'
              ? profileResponse.data 
              : profileResponse.data?.user
            
            if (profileResponse && profileResponse.success && fullUserData) {
              dispatch(setCredentials({ 
                token: data.token,
                user: fullUserData,
                role: role // Keep the authoritative role
              }))
              console.log('Full profile stored in Redux')
            } else {
              console.warn('Profile response missing user data:', profileResponse)
            }
          } catch (error) {
            console.error('Profile fetch error:', error)
             // Check for 401 specifically but be careful not to loop
            if (error.response?.status === 401) {
              dispatch(logout())
               throw new Error('Session expired. Please login again.')
            }
          }
        } else {
          console.log('Confirmation pending - profile fetch will happen after confirmation')
        }
        
        queryClient.invalidateQueries()
      }
      return data
    },
    onError: (error) => {
      console.error('Login error:', error)
      throw error
    },
  })

  const handleLogout = () => {
    // Only delete token, don't call API
    dispatch(logout())
    queryClient.clear()
  }

  return {
    token,
    user,
    isAuthenticated,
    login: loginMutation.mutate,
    loginAsync: loginMutation.mutateAsync,
    isLoggingIn: loginMutation.isPending,
    logout: handleLogout,
    loginError: loginMutation.error,
  }
}

